/**
 * Token refresh callback registry to avoid circular dependencies between authService and apiClient
 */

export type TokenRefreshCallback = () => Promise<void>;
export type AuthErrorCallback = (currentPath?: string) => void;
export type CsrfTokenGetter = () => string | null;
export type AccessTokenGetter = () => string | null;
export type AccessTokenFetcher = () => Promise<string | null>;
export type AuthState = 'logged_in' | 'logged_out' | 'expired';
export type AuthStateChangeListener = (state: AuthState) => void;

let tokenRefreshCallback: TokenRefreshCallback | null = null;
let authErrorCallback: AuthErrorCallback | null = null;
let csrfTokenGetter: CsrfTokenGetter | null = null;
let accessTokenGetter: AccessTokenGetter | null = null;
let accessTokenFetcher: AccessTokenFetcher | null = null;
const authStateChangeListeners: Set<AuthStateChangeListener> = new Set();

/**
 * Register token refresh callback - used by authService
 */
export const registerTokenRefreshCallback = (callback: TokenRefreshCallback): void => {
  tokenRefreshCallback = callback;
};

/**
 * Register auth error callback - used by authService
 */
export const registerAuthErrorCallback = (callback: AuthErrorCallback): void => {
  authErrorCallback = callback;
};

/**
 * Register CSRF token getter - used by authService
 */
export const registerCsrfTokenGetter = (getter: CsrfTokenGetter): void => {
  csrfTokenGetter = getter;
};

/**
 * Register Access token getter - used by authService to provide in-memory token
 */
export const registerAccessTokenGetter = (getter: AccessTokenGetter): void => {
  accessTokenGetter = getter;
};

/**
 * Register Access token fetcher - used by authService to provide backend bridge
 */
export const registerAccessTokenFetcher = (fetcher: AccessTokenFetcher): void => {
  accessTokenFetcher = fetcher;
};

/**
 * Register auth state change listener - used by services like ChatService
 */
export const registerAuthStateChangeListener = (listener: AuthStateChangeListener): void => {
  authStateChangeListeners.add(listener);
};

/**
 * Unregister auth state change listener
 */
export const unregisterAuthStateChangeListener = (listener: AuthStateChangeListener): void => {
  authStateChangeListeners.delete(listener);
};

/**
 * Refresh token using registered callback - used by apiClient
 */
export const refreshToken = async (): Promise<void> => {
  if (!tokenRefreshCallback) {
    throw new Error('Token refresh callback not registered');
  }
  await tokenRefreshCallback();
};

/**
 * Get CSRF token using registered getter - used by apiClient
 */
export const getCsrfTokenFromRegistry = (): string | null => {
  if (csrfTokenGetter) {
    return csrfTokenGetter();
  }
  return null;
};

/**
 * Get Access token using registered getter or fetcher
 */
export const getAccessTokenFromRegistry = async (forceFetch = false): Promise<string | null> => {
  if (!forceFetch && accessTokenGetter) {
    const token = accessTokenGetter();
    if (token) return token;
  }

  if (accessTokenFetcher) {
    return await accessTokenFetcher();
  }

  return null;
};

/**
 * Notify all listeners of an auth state change
 */
export const notifyAuthStateChange = (state: AuthState): void => {
  authStateChangeListeners.forEach((listener) => {
    try {
      listener(state);
    } catch (e) {
      console.error('Error in AuthStateChangeListener:', e);
    }
  });
};

/**
 * Trigger auth error using registered callback - used by apiClient
 */
export const triggerAuthError = (currentPath?: string): void => {
  if (authErrorCallback) {
    authErrorCallback(currentPath);
  }
};

/**
 * Clear all callbacks
 */
export const clearCallbacks = (): void => {
  tokenRefreshCallback = null;
  authErrorCallback = null;
  csrfTokenGetter = null;
  accessTokenGetter = null;
  accessTokenFetcher = null;
  authStateChangeListeners.clear();
};
